import { isMobileDevice } from '@/app/_lib/utils/isMobileDevice';
import logNativeShareError from '@/main/lib/utils/log/logNativeShareError';

interface ShareData {
  text?: string;
  title?: string;
  url?: string;
}

interface ShareResult {
  success: boolean;
  error?: string;
}

/**
 * Checks if native sharing is available.
 * Requires both navigator.share support and mobile device.
 */
export const canUseNativeShare = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    'navigator' in window &&
    'share' in window.navigator &&
    typeof window.navigator.share === 'function' &&
    isMobileDevice()
  );
};

/**
 * Triggers the native share sheet with error handling.
 */
export const shareViaWebApi = async (url: string, title: string, text?: string): Promise<ShareResult> => {
  if (!canUseNativeShare()) {
    const error = new Error('Native share not supported or not on mobile device');
    logNativeShareError(error, url);
    return { error: error.message, success: false };
  }

  if (!url) {
    const error = new Error('URL is required for sharing');
    logNativeShareError(error);
    return { error: error.message, success: false };
  }

  if (!title) {
    const error = new Error('Title is required for sharing');
    logNativeShareError(error);
    return { error: error.message, success: false };
  }

  const shareData: ShareData = { title, url };
  if (text) {
    shareData.text = text;
  }

  try {
    await window.navigator.share(shareData);
    return { success: true };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      logNativeShareError(new Error('Share cancelled by user'), url);
      return { error: 'Share was cancelled by user', success: false };
    }

    logNativeShareError(error instanceof Error ? error : new Error('An unknown error occurred during sharing'), url);
    return {
      error: error instanceof Error ? error.message : 'An unknown error occurred during sharing',
      success: false,
    };
  }
};

/**
 * Share the current page via the native share sheet.
 * Appends UTM parameters for attribution tracking.
 */
export const shareCurrentPage = async (customTitle?: string, customText?: string): Promise<ShareResult> => {
  if (typeof window === 'undefined') {
    const error = new Error('Cannot share on server-side');
    logNativeShareError(error);
    return { error: error.message, success: false };
  }

  const urlObject = new URL(window.location.href);
  urlObject.searchParams.set('utm_source', 'native_share');
  urlObject.searchParams.set('utm_medium', 'mobile');
  urlObject.searchParams.set('utm_campaign', 'social_share');
  const url = urlObject.toString();

  const title = customTitle || document.title;

  return await shareViaWebApi(url, title, customText);
};
